import PropTypes from 'prop-types';
import { useMemo, useState, useEffect, useCallback } from 'react';

import { paths } from 'src/routes/paths';
import { useRouter } from 'src/routes/hooks';

import { useAuthContext } from 'src/auth/hooks';
import { useGetUserEntityPermissions } from 'src/api/users-entities';

import { useEntityContext } from '../hooks';

export default function EntityAccessGuard({ children }) {
  const router = useRouter();
  const { authenticated, user } = useAuthContext();
  const { entity_id, parent_entity_id } = useEntityContext();
  const [checked, setChecked] = useState(false);

  const {
    permissions: parentPermissions,
    isLoading: isLoadingParentPermissions,
    error: parentPermissionsError,
  } = useGetUserEntityPermissions(user?.id, parent_entity_id);

  const {
    permissions: childPermissions,
    isLoading: isLoadingChildPermissions,
    error: childPermissionsError,
  } = useGetUserEntityPermissions(user?.id, entity_id);

  const permissions = useMemo(
    () => ({
      ...(parentPermissions && parentPermissions[0]),
      ...(childPermissions && childPermissions[0]),
      is_admin:
        (parentPermissions && parentPermissions[0]?.is_admin) ||
        (childPermissions && childPermissions[0]?.is_admin),
    }),
    [parentPermissions, childPermissions]
  );

  const isLoading = isLoadingParentPermissions || isLoadingChildPermissions;
  const error = parentPermissionsError || childPermissionsError;

  const check = useCallback(() => {
    if (!authenticated) {
      const searchParams = new URLSearchParams({ returnTo: window.location.pathname }).toString();
      const href = `${paths.auth.jwt.login}?${searchParams}`;
      router.replace(href);
      return;
    }

    // Si l'utilisateur est un superadmin, il a accès
    if (user.role === 'superadmin') {
      setChecked(true);
      return;
    }

    if (isLoading) return;
    if (error || !permissions || permissions.length === 0) {
      router.replace(paths.error.permissionDenied);
      return;
    }

    // Vérification des permissions d'accès
    if (!permissions?.is_admin) {
      router.replace(paths.error.permissionDenied);
      return;
    }

    setChecked(true);
  }, [authenticated, isLoading, error, permissions, user, router]);

  useEffect(() => {
    check();
  }, [check]);

  if (!checked) return null;

  return <>{children}</>;
}

EntityAccessGuard.propTypes = {
  children: PropTypes.node,
};
